import { useState, useRef, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import ButtonWithImg from "../buttons/ButtonWithImg";
import CustomDatePicker from "../DatePicker/CustomDatePicker";
import CustomRadioButton from "../buttons/CustomRadioButton";
import Button from "../buttons/Button";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import useTranslation from "../CustomHooks/useTranslation";
import { RootState } from "../../store";
import { useSelector } from "react-redux";

interface FilterProps {
  searchParams?: URLSearchParams;
  onFilter: (filters: { [key: string]: string | boolean | null }) => void;
  disabled: boolean;
  serviceNow?: boolean;
  loading: boolean;
  mainEndPoint?: string;
  mnoList?: boolean;
  amountList?: boolean;
  statusNotFilter?: boolean;
}

const Filter: React.FC<FilterProps> = ({
  onFilter,
  searchParams,
  disabled,
  serviceNow,
  loading,
  mainEndPoint,
  mnoList,
  amountList,
  statusNotFilter
}) => {
  const [isFilter, setIsFilter] = useState<boolean>(false);
  let { activeList } = useSelector((state: RootState) => state.activeList);
  const { translate, notEnglish } = useTranslation(breadcrumbsContent)
  const start_date = (searchParams?.get("start_date") && !mnoList && !amountList)
    ? dayjs(searchParams.get("start_date"))
    : (mnoList && !amountList && searchParams?.get("contract_start_date")) ? dayjs(searchParams.get("contract_start_date"))
        : (amountList && searchParams?.get('processed_start_date')) ? dayjs(searchParams.get('processed_start_date'))
          : null;
  const end_date = (searchParams?.get("end_date") && !mnoList && !amountList)
    ? dayjs(searchParams.get("end_date"))
    : (mnoList && searchParams?.get("contract_end_date")) ? dayjs(searchParams.get("contract_end_date"))
        : (amountList && searchParams?.get('processed_end_date')) ? dayjs(searchParams.get('processed_end_date'))
          : null;

  const purchased_start_date = (searchParams?.get("purchased_start_date") && amountList && !mnoList && activeList === 'Purchased') ? dayjs(searchParams.get("purchased_start_date")) : null;
  const purchased_end_date = (searchParams?.get("purchased_end_date") && amountList && !mnoList && activeList === 'Purchased') ? dayjs(searchParams.get("purchased_end_date")) : null;
  const status = useMemo(() => {
    if (searchParams?.get("status") === "true") return "active";
    if (searchParams?.get("status") === "false") return "inactive";
    return "all";
  }, [searchParams]);
  const service = useMemo(() => {
    if (searchParams?.get("serviceNow") === "true") {
      return "Upfront";
    } else if (searchParams?.get("serviceNow") === "false") {
      return "Prepaid";
    } else {
      return "all";
    }
  }, [searchParams]);
  const creditStatus = useMemo(() => {
    if (searchParams?.get("credit_status") === "true") {
      return "true";
    } else if (searchParams?.get("credit_status") === "false") {
      return "false";
    } else {
      return "all";
    }
  }, [searchParams]);
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(start_date);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(end_date);
  const [purchasedStartDate, setPurchasedStartDate] = useState<dayjs.Dayjs | null>(purchased_start_date);
  const [purchasedEndDate, setPurchasedEndDate] = useState<dayjs.Dayjs | null>(purchased_end_date);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<"date" | "status" | "serviceNow" | "purchased_date" | "credit_status">("date");
  const [selectedOption, setSelectedOption] = useState(status);
  const [serviceFee, setServiceFee] = useState(service);
  const [creditStatusValue, setCreditStatusValue] = useState(creditStatus);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const handleClickFilterOpen = () => {
    setIsFilter(!isFilter);
    setStartDate(start_date);
    setPurchasedStartDate(purchased_start_date);
    setEndDate(end_date);
    setPurchasedEndDate(purchased_end_date);
    setCreditStatusValue(creditStatus);
    setSelectedOption(status);
    setErrorMessage(null);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      filterRef.current &&
      !filterRef.current.contains(event.target as Node)
    ) {
      setIsFilter(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDateChange = (date: dayjs.Dayjs | null, type: string) => {
    if (type === "start") {
      setStartDate(date);
      if (date && endDate && date.isAfter(endDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "end") {
      setEndDate(date);
      if (startDate && date && date.isBefore(startDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "purchased_start") {
      setPurchasedStartDate(date);
      if (date && purchasedEndDate && date.isAfter(purchasedEndDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "purchased_end") {
      setPurchasedEndDate(date);
      if (purchasedStartDate && date && date.isBefore(purchasedStartDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    }
  };

  const preventCloseOnDatePickerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleChange = (value: string) => {
    setSelectedOption(value);
  };
  const handleServiceFee = (value: string) => {
    setServiceFee(value);
  }
  const handleCreditStatus = (value: string) => {
    setCreditStatusValue(value);
  }
  const handleApply = () => {
    if (activeFilter === "date") {
      if (!startDate && !endDate) {
        setErrorMessage("This field is required");
        return;
      }
      if (startDate && !endDate) {
        setErrorMessage("Please select end date");
        return;
      }
      if (!startDate && endDate) {
        setErrorMessage("Please select start date");
        return;
      }
      if (startDate && endDate && startDate.isAfter(endDate)) {
        setErrorMessage("Start date cannot be greater than end date");
        return;
      }
      setErrorMessage(null);
    } else if (activeFilter === "purchased_date") {
      if (!purchasedStartDate && !purchasedEndDate) {
        setErrorMessage("This field is required");
        return;
      }
      if (purchasedStartDate && !purchasedEndDate) {
        setErrorMessage("Please select end date");
        return;
      }
      if (!purchasedStartDate && purchasedEndDate) {
        setErrorMessage("Please select start date");
        return;
      }
      if (purchasedStartDate && purchasedEndDate && purchasedStartDate.isAfter(purchasedEndDate)) {
        setErrorMessage("Start date cannot be greater than end date");
        return;
      }
      setErrorMessage(null);
    } else if (activeFilter === "status") {
      setErrorMessage(null);
    } else if (activeFilter === "serviceNow") {
      setErrorMessage(null);
    } else if (activeFilter === "credit_status") {
      setErrorMessage(null);
    }
    const appliedFilters: { [key: string]: any } = {};
    if (selectedOption) {
      appliedFilters["status"] =
        selectedOption === "all"
          ? ""
          : selectedOption === "active"
            ? true
            : false;
    }
    if (serviceFee) {
      appliedFilters["service_fee_type"] = serviceFee === "all" ? "" : serviceFee;
    }
    if (creditStatusValue) {
      appliedFilters["credit_status"] = creditStatusValue === "all" ? "" : creditStatusValue;
    }
    if (startDate && !mnoList && !amountList) {
      appliedFilters["start_date"] = startDate.format("YYYY-MM-DD");
    } else if (startDate && mnoList && !amountList) {
      appliedFilters["contract_start_date"] = startDate.format("YYYY-MM-DD");
    } else if (startDate && !purchasedStartDate && amountList && !mnoList) {
      appliedFilters["processed_start_date"] = startDate.format("YYYY-MM-DD");
      appliedFilters["purchased_start_date"] = ''
    } else if (purchasedStartDate && !startDate && amountList && !mnoList) {
      appliedFilters["purchased_start_date"] = purchasedStartDate.format("YYYY-MM-DD");
      appliedFilters["processed_start_date"] = ''
    } else if (startDate && purchasedStartDate && amountList) {
      appliedFilters["processed_start_date"] = startDate.format("YYYY-MM-DD");
      appliedFilters["purchased_start_date"] = purchasedStartDate.format("YYYY-MM-DD");
    } else if (amountList) {
      appliedFilters["purchased_start_date"] = ''
      appliedFilters["processed_start_date"] = ''
    } else if (mnoList) {
      appliedFilters["contract_start_date"] = ''
    } else {
      appliedFilters["start_date"] = ''
    }
    if (endDate && !mnoList && !amountList) {
      appliedFilters["end_date"] = endDate.format("YYYY-MM-DD");
    } else if (endDate && mnoList && !amountList) {
      appliedFilters["contract_end_date"] = endDate.format("YYYY-MM-DD");
    } else if (endDate && !purchasedEndDate && amountList && !mnoList) {
      appliedFilters["processed_end_date"] = endDate.format("YYYY-MM-DD");
      appliedFilters["purchased_end_date"] = ''
    } else if (purchasedEndDate && !endDate && amountList && !mnoList) {
      appliedFilters["purchased_end_date"] = purchasedEndDate.format("YYYY-MM-DD");
      appliedFilters["processed_end_date"] = ''
    } else if (endDate && purchasedEndDate && amountList) {
      appliedFilters["processed_end_date"] = endDate.format("YYYY-MM-DD");
      appliedFilters["purchased_end_date"] = purchasedEndDate.format("YYYY-MM-DD");
    } else if (amountList) {
      appliedFilters["purchased_end_date"] = ''
      appliedFilters["processed_end_date"] = ''
    } else if (mnoList) {
      appliedFilters["contract_end_date"] = ''
    } else {
      appliedFilters["end_date"] = ''
    }
    onFilter(appliedFilters);
    setIsFilter(false);
  };

  const handleClearAll = () => {
    setStartDate(null);
    setEndDate(null);
    setPurchasedStartDate(null);
    setPurchasedEndDate(null);
    setErrorMessage(null);
    setSelectedOption("all");
    setServiceFee("all");
    setCreditStatusValue("all");
    setActiveFilter("date");
    onFilter({ status: "", end_date: "", start_date: "", service_fee_type: "", contract_start_date: "", contract_end_date: "", purchased_start_date: "", purchased_end_date: "", processed_start_date: "", processed_end_date: "", credit_status: "" });
    setIsFilter(false);
  };
  const ClearButtonDisable =
    !errorMessage &&
    startDate === null &&
    endDate === null &&
    purchasedStartDate === null &&
    purchasedEndDate === null &&
    selectedOption === "all" &&
    serviceFee === "all" && creditStatusValue === "all";

  const handleFilterActive = (type: any) => {
    setActiveFilter(type);
    if (!startDate && !endDate || startDate && !endDate || !startDate && endDate || startDate && endDate && startDate.isAfter(endDate)) {
      setStartDate(null);
      setEndDate(null);
    } else if (!purchasedStartDate && !purchasedEndDate || purchasedStartDate && !purchasedEndDate || !purchasedStartDate && purchasedEndDate || purchasedStartDate && purchasedEndDate && purchasedStartDate.isAfter(purchasedEndDate)) {
      setPurchasedStartDate(null);
      setPurchasedEndDate(null);
    }
    setErrorMessage(null);
  };
  
  return (
    <div className="flex justify-end">
      <div className="relative" ref={filterRef}>
        <ButtonWithImg
          buttonName={translate("Filter")}
          ImgSrc={'filter'}
          className="px-4"
          buttonOnclick={handleClickFilterOpen}
          testId={'filter'}
        />
        {isFilter && (
          <div className={`py-[16px] px-4 bg-white border border-s_dark_hover shadow-lg rounded-lg absolute ${notEnglish ? 'left-0' : 'right-0'} mt-2 z-20`}>
            <div className="w-[588px]">
              <p className="text-heading4 text-primary_text mb-3">{translate('Filter By')}</p>
              <hr className="2xl:mb-4 mb-2 border-neutral-300" />
              <div className="flex">
                <div className={`${notEnglish ? 'border-l ml-4' : 'border-r'}`}>
                  <p
                    data-testid='date_range'
                    className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "date"
                      ? "text-primary_text"
                      : "text-secondary_text"
                      }`}
                    onClick={() => handleFilterActive("date")}
                  >
                    {!amountList ? translate('Date Range') : translate('Processed Date')}
                  </p>
                  {(amountList && activeList === 'Purchased') && <>
                    <p
                    data-testid='date_range'
                    className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "purchased_date"
                      ? "text-primary_text"
                      : "text-secondary_text"
                      }`}
                    onClick={() => handleFilterActive("purchased_date")}
                  >
                    {translate('Purchased Date')}
                  </p>
                  <p
                    data-testid='credit_status'
                    className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "credit_status"
                      ? "text-primary_text"
                      : "text-secondary_text"
                      }`}
                    onClick={() => handleFilterActive("credit_status")}
                  >
                    {translate('Credit Status')}
                  </p>
                  </>}
                  {(!mnoList && !amountList && !statusNotFilter) && <p
                    className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "status"
                      ? "text-primary_text"
                      : "text-secondary_text"
                      }`}
                    data-testid='status'
                    onClick={() => handleFilterActive("status")}
                  >
                    {translate('Status')}
                  </p>}
                  {serviceNow && (
                    <p
                      className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "serviceNow"
                        ? "text-primary_text"
                        : "text-secondary_text"
                        }`}
                      data-testid='service_fee_type'
                      onClick={() => handleFilterActive("serviceNow")}
                    >
                      Service Fee Type
                    </p>
                  )}
                </div>
                <div
                  className="pl-4 max-w-[394px] w-full"
                  style={{ minHeight: "100px" }}
                >
                  {activeFilter === "date" && (
                    <div
                      className="flex justify-between gap-3"
                      onMouseDown={preventCloseOnDatePickerClick}
                    >
                      <CustomDatePicker
                        testID="start-date"
                        label={translate('Start Date')}
                        handleStates={handleDateChange}
                        value={startDate}
                        error={
                          errorMessage === "Please select start date"
                            ? "Please select start date"
                            : ''
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="start"
                        required
                        classType={'filter'}
                      />
                      <CustomDatePicker
                        testID="end-date"
                        label={translate('End Date')}
                        handleStates={handleDateChange}
                        value={endDate}
                        required
                        error={
                          errorMessage === "Please select end date"
                            ? "Please select end date"
                            : ''
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="end"
                        classType={'filter'}
                      />
                    </div>
                  )}
                  {(activeFilter === "purchased_date" && activeList === 'Purchased') && (
                    <div
                      className="flex justify-between gap-3"
                      onMouseDown={preventCloseOnDatePickerClick}
                    >
                      <CustomDatePicker
                        testID="start-date"
                        label={translate('Start Date')}
                        handleStates={handleDateChange}
                        value={purchasedStartDate}
                        error={
                          errorMessage === "Please select start date"
                            ? "Please select start date"
                            : ''
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="purchased_start"
                        required
                        classType={'filter'}
                      />
                      <CustomDatePicker
                        testID="end-date"
                        label={translate('End Date')}
                        handleStates={handleDateChange}
                        value={purchasedEndDate}
                        required
                        error={
                          errorMessage === "Please select end date"
                            ? "Please select end date"
                            : ''
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="purchased_end"
                        classType={'filter'}
                      />
                    </div>
                  )}
                  {(activeFilter === "credit_status" && activeList === 'Purchased') && (
                    <div>
                      <CustomRadioButton
                        options={[
                          { id: "all", label: translate("All") },
                          { id: "true", label: translate("Received") },
                          { id: "false", label: translate("Pending") },
                        ]}
                        selected={creditStatusValue}
                        onChange={handleCreditStatus}
                      />
                    </div>
                  )}
                  {activeFilter === "status" && (
                    <div>
                      <CustomRadioButton
                        options={[
                          { id: "all", label: translate("All") },
                          { id: "active", label: translate("Active") },
                          { id: "inactive", label: translate("Inactive") },
                        ]}
                        selected={selectedOption}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                  {activeFilter === "serviceNow" && (
                    <div>
                      <CustomRadioButton
                        options={[
                          { id: "all", label: "All" },
                          { id: "Upfront", label: "Upfront" },
                          { id: "Postpaid", label: "Postpaid" },
                        ]}
                        selected={serviceFee}
                        onChange={handleServiceFee}
                      />
                    </div>
                  )}
                  {errorMessage !== "Please select start date" &&
                    errorMessage !== "Please select end date" &&
                    errorMessage && (
                      <div className="pt-1">
                        <ErrorMessage errorMessage={errorMessage} />
                      </div>
                    )}
                </div>
              </div>
              <div className="gap-3 flex pt-6">
                <Button
                  buttonName={translate("Clear All")}
                  textSize="text-large"
                  buttonHight="h-10"
                  testId="clear_all"
                  textColor={ClearButtonDisable ? "" : "text-p_primary"}
                  className={`px-4 ${ClearButtonDisable ? "" : "bg-white border border-p_primary"
                    } `}
                  buttonOnclick={handleClearAll}
                  buttonDisabled={ClearButtonDisable}
                />
                <Button
                  buttonName={translate("Apply")}
                  textSize="text-large"
                  buttonHight="h-10"
                  testId="apply_filter"
                  className={`px-4 bg-p_primary text-white ${notEnglish ? 'w-fit' : 'w-24'}`}
                  buttonOnclick={handleApply}
                  buttonDisabled={!!errorMessage}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Filter;
